import type { FC, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { DashboardWidgetType } from 'src/constants/dashboard';
import type { UpstreamApiCrossSurveyBenchmarkData } from 'src/types/dashboard';
import {
  activeCrossSurveyBenchmarkWidgets,
  dashboardContainsWidgetTypes,
} from 'src/components/dashboard/DashboardContext/helper';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext/index';
import { useDashboardFilters } from 'src/components/dashboard/hooks';
import useCrossSurveyBenchmarkData from 'src/components/dashboard/hooks/widgetData/useCrossSurveyBenchmarkData';

export interface CrossSurveyBenchmarkValue {
  data: UpstreamApiCrossSurveyBenchmarkData | undefined;
  errorMsg: string;
  loading: boolean;
  refetch: () => void;
}

export const defaultCrossSurveyBenchmarkValue: CrossSurveyBenchmarkValue = {
  data: undefined,
  errorMsg: '',
  loading: false,
  refetch: () => null,
};

export const CrossSurveyBenchmarkContext =
  createContext<CrossSurveyBenchmarkValue>(defaultCrossSurveyBenchmarkValue);

const crossSurveyBenchmarkWidgets = [
  DashboardWidgetType.DOUGHNUT,
  DashboardWidgetType.SPEEDOMETER_RE,
];

export const CrossSurveyBenchmarkProvider: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const { dashboardConfig } = useDashboardContext();

  const isEnabled = useMemo(
    () =>
      dashboardContainsWidgetTypes(
        dashboardConfig,
        crossSurveyBenchmarkWidgets,
      ) && activeCrossSurveyBenchmarkWidgets(dashboardConfig),
    [dashboardConfig],
  );

  const { flatFiltersDebounced } = useDashboardFilters();
  const crossSurveyBenchmarkData = useCrossSurveyBenchmarkData(
    dashboardConfig?.id,
    flatFiltersDebounced,
    isEnabled,
  );

  return (
    <CrossSurveyBenchmarkContext.Provider value={crossSurveyBenchmarkData}>
      {children}
    </CrossSurveyBenchmarkContext.Provider>
  );
};

export const useCrossSurveyBenchmark = () =>
  useContext(CrossSurveyBenchmarkContext);

export default CrossSurveyBenchmarkProvider;
